import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FiEdit3, FiLoader } from "react-icons/fi";
import { hotkeyLabel } from "./aiEditHotkey";

const INSET = 16;
const PILL_HEIGHT = 36;

/**
 * Bottom corner of the editor panel (right by default; left for Agent Mode in
 * an RTL language), clamped to the window; hidden while the editor is off screen.
 */
export function computePosition(anchor, side = "right") {
  if (!anchor) return null;
  const rect = anchor.getBoundingClientRect();
  const visBottom = window.innerHeight;
  if (rect.bottom < PILL_HEIGHT + INSET * 2 || rect.top > visBottom - PILL_HEIGHT - INSET) return null;
  const top = Math.max(Math.min(rect.bottom, visBottom) - PILL_HEIGHT - INSET, rect.top + INSET);
  if (side === "left") return { top, left: Math.max(INSET, rect.left + INSET) };
  return { top, right: Math.max(INSET, window.innerWidth - rect.right + INSET) };
}

function useFloatingPosition(anchorRef, enabled, side) {
  const [pos, setPos] = useState(null);
  const update = useCallback(
    () => setPos(enabled ? computePosition(anchorRef?.current, side) : null),
    [anchorRef, enabled, side],
  );
  useLayoutEffect(() => {
    update();
  }, [update]);
  useEffect(() => {
    if (!enabled) return undefined;
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    // The editor may mount after this control: observe it as soon as it exists.
    let observed = false;
    const attach = () => {
      if (observed || !anchorRef?.current) return;
      observed = true;
      ro?.observe(anchorRef.current);
      schedule();
    };
    attach();
    const poll = observed ? null : setInterval(() => {
      attach();
      if (observed) clearInterval(poll);
    }, 300);
    return () => {
      if (poll) clearInterval(poll);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      ro?.disconnect();
    };
  }, [update, anchorRef, enabled]);
  return pos;
}

/**
 * AI Edit = a mode of the editor microphone. One floating pill in the editor's
 * bottom-right corner for every CRE case (normal CRE, Agent Mode, iframes),
 * on screen while the editor is visible.
 * Off: neutral + hotkey. On: green + live dot. Applying: spinner.
 */
export default function AiEditModeToggle({ active, busy, onToggle, hotkey, anchorRef, side = "right" }) {
  const { t } = useTranslation("common");
  const keys = hotkeyLabel(hotkey);
  const pos = useFloatingPosition(anchorRef, true, side);

  const label = t("editReportAi.button", { defaultValue: "AI Edit" });
  const title = active
    ? t("editReportAi.toggleOn", { defaultValue: "AI Edit is on — click to leave AI Edit" })
    : t("editReportAi.toggleOff", { defaultValue: "AI Edit — speak changes to the report" });
  const tone = active
    ? "border-[#A4C639] bg-[#A4C639] text-gray-900 dark:bg-[#CBEA7B]"
    : "border-gray-200 bg-white/95 text-gray-800 hover:border-[#A4C639] hover:bg-[#CBEA7B80] dark:border-gray-600 dark:bg-gray-700/95 dark:text-gray-100 dark:hover:bg-gray-600";

  const content = (
    <>
      {busy ? <FiLoader className="h-4 w-4 animate-spin" aria-hidden /> : <FiEdit3 className="h-4 w-4" aria-hidden />}
      <span>{label}</span>
      {active ? (
        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" aria-hidden />
      ) : (
        <span className="font-mono text-[10px] font-normal opacity-60">{keys}</span>
      )}
    </>
  );
  const common = {
    type: "button",
    onClick: () => onToggle?.(),
    "aria-pressed": !!active,
    "aria-label": `${label} (${keys})`,
    title: `${title} (${keys})`,
    "data-testid": "ai-edit-toggle",
    dir: "ltr",
  };

  if (!pos || typeof document === "undefined") return null;
  return createPortal(
    <button
      {...common}
      style={{ top: pos.top, ...(pos.left != null ? { left: pos.left } : { right: pos.right }), height: PILL_HEIGHT }}
      data-side={pos.left != null ? "left" : "right"}
      className={`fixed z-[60] inline-flex items-center gap-2 rounded-full border px-3.5 text-[13px] font-semibold shadow-md backdrop-blur transition-colors ${tone}`}
    >
      {content}
    </button>,
    document.body,
  );
}
